import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Stack,
  Paper,
  Grow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  Switch,
  FormControlLabel,
  Tooltip,
  useTheme,
} from '@mui/material';
import { Sparkles, Copy, Check } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import { config } from '../../../config';
import { ProgressModal, FeedbackSnackbar, GEMINI_MODEL } from './common';

interface DescriptionVariant {
  text: string;
}

interface GeneratorState {
  loading: boolean;
  progress: number;
  complete: boolean;
  error: string | null;
}

const YouTubeDescriptionGenerator: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [prompt, setPrompt] = useState('');
  const [variants, setVariants] = useState<DescriptionVariant[]>([]);
  const [state, setState] = useState<GeneratorState>({
    loading: false,
    progress: 0,
    complete: false,
    error: null,
  });
  const [language, setLanguage] = useState('English');
  const [numVariants, setNumVariants] = useState(3);
  const [addHashtags, setAddHashtags] = useState(true);
  const [copySuccess, setCopySuccess] = useState<number | null>(null);
  const progressInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  const generateDescriptions = async () => {
    if (!prompt.trim()) {
      setState((prev) => ({
        ...prev,
        error: t('youtubeDescriptionGenerator.errors.emptyPrompt'),
      }));
      return;
    }

    try {
      setState({
        loading: true,
        progress: 0,
        complete: false,
        error: null,
      });

      // Simulate progress
      let progress = 0;
      progressInterval.current = setInterval(() => {
        progress += Math.random() * 15;
        if (progress > 95) {
          progress = 95;
          if (progressInterval.current) {
            clearInterval(progressInterval.current);
          }
        }
        setState((prev) => ({ ...prev, progress }));
      }, 500);

      // Call Gemini API
      const response = await fetch(config.geminiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: GEMINI_MODEL,
          contents: [
            {
              parts: [
                {
                  text: `You are a professional YouTube content creator assistant. Generate ${numVariants} compelling YouTube video descriptions for a video about: "${prompt}".
                  
                  Write in ${language} language.
                  ${
                    addHashtags
                      ? 'Include 3-5 relevant hashtags at the end of each description.'
                      : 'Do not include any hashtags.'
                  }
                  
                  Each description should:
                  1. Be engaging and hook viewers in the first line
                  2. Include relevant keywords for SEO
                  3. Be between 100-200 words
                  4. Have a clear call-to-action
                  5. Be formatted with appropriate line breaks
                  
                  Format your response as a JSON array with each description as a separate object with a "text" field. Do not include any explanations or other text outside the JSON array.
                  Example format:
                  [{"text": "First description here..."}, {"text": "Second description here..."}, {"text": "Third description here..."}]`,
                },
              ],
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate descriptions');
      }

      const data = await response.json();
      const result = data.candidates[0].content.parts[0].text;

      // Parse the JSON response
      try {
        // Extract JSON from the response (in case there's any extra text)
        const jsonMatch = result.match(/\[(.*\n*)*\]/);
        if (jsonMatch) {
          const parsedVariants = JSON.parse(jsonMatch[0]);
          setVariants(parsedVariants);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('Failed to parse generated descriptions');
      }

      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }

      setState({
        loading: false,
        progress: 100,
        complete: true,
        error: null,
      });
    } catch (error) {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
      setState({
        loading: false,
        progress: 0,
        complete: false,
        error: 'Failed to generate descriptions. Please try again.',
      });
    }
  };

  const handleCopy = (index: number) => {
    if (variants[index]) {
      navigator.clipboard.writeText(variants[index].text);
      setCopySuccess(index);
      setTimeout(() => setCopySuccess(null), 2000);
    }
  };

  const handleReset = () => {
    setPrompt('');
    setVariants([]);
    setState({
      loading: false,
      progress: 0,
      complete: false,
      error: null,
    });
  };

  return (
    <Box sx={{ textAlign: 'center', p: 3, maxWidth: 800, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: 'none',
          borderRadius: 2,
          mb: 4,
          backgroundColor: 'white',
        }}
      >
        <Stack spacing={3}>
          <TextField
            fullWidth
            label={t(
              'youtubeDescriptionGenerator.promptLabel',
              'Describe this video...'
            )}
            placeholder={t(
              'youtubeDescriptionGenerator.promptPlaceholder',
              "e.g. Why it's important to eat broccoli"
            )}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            multiline
            rows={4}
            variant="outlined"
            disabled={state.loading}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.palette.background.default,
                borderRadius: 1,
              },
            }}
          />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={2}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel id="language-select-label">
                  {t('youtubeDescriptionGenerator.languageLabel', 'Language')}
                </InputLabel>
                <Select
                  labelId="language-select-label"
                  value={language}
                  label={t(
                    'youtubeDescriptionGenerator.languageLabel',
                    'Language'
                  )}
                  onChange={(e) => setLanguage(e.target.value)}
                  disabled={state.loading}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    backgroundColor: theme.palette.background.default,
                    borderRadius: 1,
                  }}
                >
                  <MenuItem value="English">English</MenuItem>
                  <MenuItem value="Spanish">Spanish</MenuItem>
                  <MenuItem value="French">French</MenuItem>
                  <MenuItem value="German">German</MenuItem>
                  <MenuItem value="Chinese">Chinese</MenuItem>
                  <MenuItem value="Japanese">Japanese</MenuItem>
                  <MenuItem value="Korean">Korean</MenuItem>
                  <MenuItem value="Russian">Russian</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel id="variants-select-label">
                  {t('youtubeDescriptionGenerator.variantsLabel', 'Variants')}
                </InputLabel>
                <Select
                  labelId="variants-select-label"
                  value={numVariants}
                  label={t(
                    'youtubeDescriptionGenerator.variantsLabel',
                    'Variants'
                  )}
                  onChange={(e) => setNumVariants(Number(e.target.value))}
                  disabled={state.loading}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    backgroundColor: theme.palette.background.default,
                    borderRadius: 1,
                  }}
                >
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={2}>2</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                  <MenuItem value={5}>5</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={addHashtags}
                  onChange={(e) => setAddHashtags(e.target.checked)}
                  disabled={state.loading}
                />
              }
              label={t(
                'youtubeDescriptionGenerator.addHashtags',
                'Add hashtags'
              )}
              sx={{ ml: 0 }}
            />
          </Stack>

          <Button
            variant="contained"
            color="primary"
            onClick={generateDescriptions}
            disabled={state.loading || !prompt.trim()}
            startIcon={<Sparkles />}
            size="large"
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 28,
              alignSelf: 'center',
              minWidth: 200,
            }}
          >
            {t('youtubeDescriptionGenerator.generateButton', 'Generate')}
          </Button>
        </Stack>
      </Paper>

      {variants.length > 0 && (
        <Grow in={variants.length > 0} timeout={500}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {t(
                'youtubeDescriptionGenerator.generatedDescriptions',
                'Generated Descriptions'
              )}
            </Typography>
            <Stack spacing={3} sx={{ mt: 3 }}>
              {variants.map((variant, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 3,
                    border: 'none',
                    borderRadius: 2,
                    textAlign: 'left',
                    position: 'relative',
                    backgroundColor: 'white',
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {t('youtubeDescriptionGenerator.variantLabel', 'Variant')}{' '}
                    {index + 1}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-line',
                      mb: 2,
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    {variant.text}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Tooltip
                      title={
                        copySuccess === index
                          ? t('youtubeDescriptionGenerator.copied', 'Copied!')
                          : t('youtubeDescriptionGenerator.copy', 'Copy')
                      }
                    >
                      <IconButton
                        onClick={() => handleCopy(index)}
                        color={copySuccess === index ? 'success' : 'default'}
                        size="small"
                      >
                        {copySuccess === index ? <Check /> : <Copy />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Box>
        </Grow>
      )}

      <ProgressModal
        open={state.loading}
        progress={state.progress}
        isComplete={state.complete}
        processingMessage={t(
          'youtubeDescriptionGenerator.generating',
          'Generating descriptions...'
        )}
        completeMessage={t(
          'youtubeDescriptionGenerator.generationComplete',
          'Your descriptions are ready!'
        )}
        onReset={handleReset}
      />

      <FeedbackSnackbar
        open={!!state.error}
        message={state.error || ''}
        severity="error"
        onClose={() => setState((prev) => ({ ...prev, error: null }))}
      />
    </Box>
  );
};

export default YouTubeDescriptionGenerator;
